import type { PropsWithChildren, ReactNode } from 'react';
import { RawIntlProvider } from 'react-intl';
import { useEmberIntl } from '@qonto/react-migration-toolkit/react/hooks/providers';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PolymorphicSegmentProvider } from '@repo/poly-hooks/segment';
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { EmberServicesProvider } from '@repo/poly-hooks/contexts/ember-services-context.tsx';
import { NavigateProvider } from '@repo/poly-hooks/routing';
import { createEmberNavigateFn } from '@repo/poly-hooks/routing/-ember/get-navigate';
import { SentryProvider } from 'receivable-invoices-js/react/contexts/sentry';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // default retry is 3: https://tanstack.com/query/latest/docs/framework/react/guides/query-retries
      retry: 0,
    },
  },
});

export function CustomProviders({ children }: PropsWithChildren): ReactNode {
  const intl = useEmberIntl();
  const segment = useEmberService('segment');
  const sentry = useEmberService('sentry');
  // eslint-disable-next-line @repo/no-use-ember-service-router -- needed for ember services provider
  const emberRouter = useEmberService('router');

  const navigate = createEmberNavigateFn(emberRouter);

  const hasQueryDevtools = localStorage.getItem('QUERY_DEV_TOOLS') === 'true';

  return (
    <EmberServicesProvider emberRouter={emberRouter}>
      <SentryProvider sentry={sentry}>
        <RawIntlProvider value={intl}>
          <NavigateProvider navigate={navigate}>
            <PolymorphicSegmentProvider segment={segment}>
              <QueryClientProvider client={queryClient}>
                {children}
                {hasQueryDevtools ? <ReactQueryDevtools buttonPosition="bottom-left" /> : null}
              </QueryClientProvider>
            </PolymorphicSegmentProvider>
          </NavigateProvider>
        </RawIntlProvider>
      </SentryProvider>
    </EmberServicesProvider>
  );
}
